// useFolder.tsx -- the catalog tree of a single folder, plus the operations
// the desktop performs against it (reposition, refresh, navigate, move,
// delete, restore).
//
// One instance per folder being viewed. Used by Desktop.tsx to render entries
// at their saved positions, and by useDesktop's drag handlers to reach the
// move/delete API endpoints.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api.js';
export function useFolder(projectId, folderId) {
    const [tree, setTree] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const refresh = useCallback(async () => {
        if (!folderId)
            return;
        setLoading(true);
        setError(null);
        try {
            const r = await api.get(`/p/${projectId}/folders/${folderId}/tree?depth=1`);
            setTree(r.tree);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'unknown error');
        }
        finally {
            setLoading(false);
        }
    }, [projectId, folderId]);
    useEffect(() => {
        void refresh();
    }, [refresh]);
    // Optimistic local mutations. Each calls the BFF then re-applies result.
    // For mutations that change which folder an entry lives in (trash, move),
    // we just refresh; the destination folder will reload on its own next mount.
    const reposition = useCallback(async (itemId, x, y) => {
        if (!folderId)
            return;
        // Optimistic: update local entries immediately so the icon doesn't
        // snap back to the original position while the API is in flight.
        setTree((prev) => {
            if (!prev)
                return prev;
            return {
                ...prev,
                entries: prev.entries.map((e) => e.itemId === itemId
                    ? { ...e, attrs: { ...(e.attrs ?? {}), position: { x, y } } }
                    : e),
            };
        });
        await api.patch(`/p/${projectId}/folders/${folderId}/entries/${itemId}`, {
            position: { x, y },
        });
    }, [projectId, folderId]);
    const rename = useCallback(async (itemId, displayName) => {
        if (!folderId)
            return;
        await api.patch(`/p/${projectId}/folders/${folderId}/entries/${itemId}`, {
            displayName,
        });
        await refresh();
    }, [projectId, folderId, refresh]);
    const trashItem = useCallback(async (itemId) => {
        if (!folderId)
            return;
        await api.post(`/p/${projectId}/folders/${folderId}/entries/${itemId}/trash`);
        await refresh();
    }, [projectId, folderId, refresh]);
    const restoreItem = useCallback(async (itemId) => {
        if (!folderId)
            return;
        await api.post(`/p/${projectId}/folders/${folderId}/entries/${itemId}/restore`);
        await refresh();
    }, [projectId, folderId, refresh]);
    const moveTo = useCallback(async (itemId, targetFolderId) => {
        if (!folderId)
            return;
        await api.post(`/p/${projectId}/folders/${folderId}/entries/${itemId}/move`, { targetFolderId });
        await refresh();
    }, [projectId, folderId, refresh]);
    return useMemo(() => ({
        folderId,
        tree,
        loading,
        error,
        refresh,
        reposition,
        rename,
        trashItem,
        restoreItem,
        moveTo,
    }), [folderId, tree, loading, error, refresh, reposition, rename, trashItem, restoreItem, moveTo]);
}
